import { useEffect, useMemo, useState } from "react";
import {
  AppButton,
  AppTable,
  AppTableCell,
  AppTableHeadCell,
  AppTableRow,
  FormField,
  RowNumberCell,
  SearchField,
  StatusChip,
  TABLE_ROWS_PER_PAGE_OPTIONS,
  toast,
} from "../../components/ui";
import {
  Box,
  Checkbox,
  Drawer,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import {
  ClioService,
  ClioTaskTypeRequest,
  ClioTaskTypeResponse,
} from "../../api";

const DEFAULT_ROWS_PER_PAGE = 10;

const TABLE_COLUMNS = [
  { id: "id", label: "ID" },
  { id: "name", label: "Name" },
  { id: "status", label: "Status" },
  { id: "automation", label: "Automation" },
  { id: "updatedAt", label: "Updated" },
];

type EditorState = {
  mode: "create" | "edit";
  id?: number;
  name: string;
  enabled: boolean;
};

type TypeSortKey = (typeof TABLE_COLUMNS)[number]["id"];

function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") {
    return Number(a) - Number(b);
  }
  return String(a).localeCompare(String(b), undefined, {
    sensitivity: "base",
    numeric: true,
  });
}

function getTypeSortValue(row: ClioTaskTypeResponse, key: TypeSortKey): unknown {
  switch (key) {
    case "status":
      return row.enabled !== false;
    case "automation":
      return !!row.inAppEnum;
    case "updatedAt":
      return row.updatedAt ? dayjs(row.updatedAt).valueOf() : null;
    case "id":
      return row.id;
    case "name":
    default:
      return row.name ?? "";
  }
}

export default function ClioTaskTypesTable() {
  const [allTypes, setAllTypes] = useState<ClioTaskTypeResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [sortBy, setSortBy] = useState<TypeSortKey>("name");
  const [sortAsc, setSortAsc] = useState(true);

  const loadTypes = async () => {
    setLoading(true);
    try {
      const types = await ClioService.getApiClioManagedTaskTypes();
      setAllTypes(types ?? []);
    } catch {
      toast.error("Could not load Clio task types.");
      setAllTypes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadTypes();
  }, []);

  const filteredTypes = useMemo(() => {
    const lower = searchTerm.trim().toLowerCase();
    const filtered = !lower
      ? [...allTypes]
      : allTypes.filter(
          (t) =>
            t.name?.toLowerCase().includes(lower) ||
            String(t.id).includes(lower)
        );

    filtered.sort((a, b) => {
      const result = compareValues(
        getTypeSortValue(a, sortBy),
        getTypeSortValue(b, sortBy)
      );
      return sortAsc ? result : -result;
    });
    return filtered;
  }, [allTypes, searchTerm, sortBy, sortAsc]);

  const maxPage = Math.max(0, Math.ceil(filteredTypes.length / rowsPerPage) - 1);
  const safePage = Math.min(page, maxPage);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, sortBy, sortAsc]);

  const pageRows = filteredTypes.slice(
    safePage * rowsPerPage,
    safePage * rowsPerPage + rowsPerPage
  );

  const handleRequestSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortAsc((prev) => !prev);
    } else {
      setSortBy(columnId as TypeSortKey);
      setSortAsc(true);
    }
  };

  const openCreate = () => {
    setEditor({ mode: "create", name: "", enabled: true });
  };

  const openEdit = (row: ClioTaskTypeResponse) => {
    setEditor({
      mode: "edit",
      id: row.id,
      name: row.name ?? "",
      enabled: row.enabled !== false,
    });
  };

  const handleSave = async () => {
    if (!editor) return;
    const name = editor.name.trim();
    if (!name) {
      toast.warning("Name is required.");
      return;
    }
    if (name.length > 50) {
      toast.warning("Name must be 50 characters or fewer.");
      return;
    }

    const body: ClioTaskTypeRequest = {
      name,
      enabled: editor.enabled,
    };

    setSaving(true);
    try {
      if (editor.mode === "create") {
        const created = await ClioService.postApiClioManagedTaskType({ name });
        setAllTypes((prev) =>
          [...prev, created].sort((a, b) =>
            (a.name ?? "").localeCompare(b.name ?? "", undefined, {
              sensitivity: "base",
            })
          )
        );
        toast.success("Task type created in Clio.");
      } else if (editor.id != null) {
        const updated = await ClioService.patchApiClioManagedTaskType(
          editor.id,
          body
        );
        setAllTypes((prev) =>
          prev.map((t) => (t.id === updated.id ? updated : t))
        );
        toast.success("Task type updated.");
      }
      setEditor(null);
    } catch {
      toast.error(
        editor.mode === "create"
          ? "Could not create task type."
          : "Could not update task type."
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (row: ClioTaskTypeResponse) => {
    const nextEnabled = row.enabled === false;
    try {
      const updated = await ClioService.patchApiClioManagedTaskType(row.id, {
        name: row.name ?? "",
        enabled: nextEnabled,
      });
      setAllTypes((prev) =>
        prev.map((t) => (t.id === updated.id ? updated : t))
      );
      toast.success(nextEnabled ? "Task type enabled." : "Task type disabled.");
    } catch {
      toast.error("Could not update task type status.");
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <SearchField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Stack direction="row" spacing={1}>
          <AppButton
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => void loadTypes()}
            disabled={loading}
          >
            Refresh
          </AppButton>
          <AppButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            New task type
          </AppButton>
        </Stack>
      </Stack>

      <AppTable
        columns={TABLE_COLUMNS}
        showRowNumber
        sortBy={sortBy}
        sortAsc={sortAsc}
        onRequestSort={handleRequestSort}
        trailingHeadCells={
          <AppTableHeadCell align="right">Actions</AppTableHeadCell>
        }
        extraColumnCount={1}
        loading={loading}
        loadingMessage="Loading Clio task types…"
        isEmpty={!loading && filteredTypes.length === 0}
        emptyTitle="No task types found"
        emptyDescription={
          searchTerm
            ? "Try a different search."
            : "Create a task type to get started."
        }
        pagination={{
          count: filteredTypes.length,
          page: safePage,
          rowsPerPage,
          rowsPerPageOptions: [...TABLE_ROWS_PER_PAGE_OPTIONS],
          onPageChange: setPage,
          onRowsPerPageChange: (next) => {
            setRowsPerPage(next);
            setPage(0);
          },
        }}
      >
        {pageRows.map((row, index) => (
          <AppTableRow key={row.id} hover>
            <RowNumberCell value={safePage * rowsPerPage + index + 1} />
            <AppTableCell>{row.id}</AppTableCell>
            <AppTableCell sx={{ fontWeight: 600 }}>{row.name}</AppTableCell>
            <AppTableCell>
              {row.enabled !== false ? (
                <StatusChip label="Enabled" tone="dirtyGreen" size="small" />
              ) : (
                <StatusChip
                  label="Disabled"
                  tone="neutral"
                  variant="outlined"
                  size="small"
                />
              )}
            </AppTableCell>
            <AppTableCell>
              {row.inAppEnum ? (
                <StatusChip label="Yes" tone="secondary" size="small" />
              ) : (
                <StatusChip
                  label="No"
                  tone="neutral"
                  variant="outlined"
                  size="small"
                />
              )}
            </AppTableCell>
            <AppTableCell sx={{ whiteSpace: "nowrap" }}>
              {row.updatedAt
                ? dayjs(row.updatedAt).format("MM/DD/YYYY")
                : "—"}
            </AppTableCell>
            <AppTableCell align="right">
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <AppButton
                  variant="outlined"
                  size="small"
                  onClick={() => openEdit(row)}
                >
                  Edit
                </AppButton>
                <AppButton
                  variant="outlined"
                  size="small"
                  color={row.enabled !== false ? "inherit" : "primary"}
                  onClick={() => void toggleEnabled(row)}
                >
                  {row.enabled !== false ? "Disable" : "Enable"}
                </AppButton>
              </Stack>
            </AppTableCell>
          </AppTableRow>
        ))}
      </AppTable>

      {editor && (
        <Drawer
          anchor="right"
          open
          onClose={() => !saving && setEditor(null)}
          PaperProps={{
            sx: {
              width: { xs: "100%", sm: 420 },
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {editor.mode === "create"
                  ? "New Clio task type"
                  : "Edit task type"}
              </Typography>
            </Box>
            <IconButton
              aria-label="Close"
              onClick={() => !saving && setEditor(null)}
              size="small"
              disabled={saving}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
            <Stack spacing={2}>
              <FormField
                label="Name"
                value={editor.name}
                onChange={(e) =>
                  setEditor((prev) =>
                    prev ? { ...prev, name: e.target.value } : prev
                  )
                }
                inputProps={{ maxLength: 50 }}
                helperText={`${editor.name.trim().length}/50 characters`}
                required
                autoFocus
              />
              {editor.mode === "edit" && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editor.enabled}
                      onChange={(e) =>
                        setEditor((prev) =>
                          prev ? { ...prev, enabled: e.target.checked } : prev
                        )
                      }
                    />
                  }
                  label="Enabled in Clio"
                />
              )}
            </Stack>
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "grey.50",
            }}
          >
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <AppButton
                onClick={() => setEditor(null)}
                variant="outlined"
                disabled={saving}
              >
                Cancel
              </AppButton>
              <AppButton
                onClick={() => void handleSave()}
                variant="contained"
                disabled={saving}
              >
                {saving ? "Saving…" : "Save"}
              </AppButton>
            </Stack>
          </Box>
        </Drawer>
      )}
    </Box>
  );
}
